import { Box, Flex, Group, Stack, StackProps } from '@mantine/core';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import H3Medium from 'components/typography/H3Medium';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * The props of the FormInputGroup.
 */
export interface FormInputGroupProps {
  groupTitle: string | React.ReactNode;
  groupTooltip?: string;
  infoBox?: React.ReactNode;
  children?: React.ReactNode | React.ReactNode[];
  stackProps?: StackProps;
  withoutLeftBorder?: boolean;
}

/**
 * Input group with the title, left border and children.
 */
export default function FormInputGroup({
  groupTitle,
  groupTooltip,
  infoBox,
  children,
  stackProps,
  withoutLeftBorder,
}: FormInputGroupProps) {
  const { isMobile, isTablet } = useResponsiveDesign();

  return (
    <Stack
      pt={isMobile ? 12 : isTablet ? 12 : 24}
      pb={isMobile ? 12 : isTablet ? 12 : 24}
      pl={isMobile ? 16 : isTablet ? 20 : 40}
      pr={isMobile ? 16 : isTablet ? 12 : 24}
      spacing={isMobile ? 12 : isTablet ? 'sm' : 'xl'}
      {...stackProps}
    >
      <Group spacing={isMobile ? 8 : 16} noWrap={!isMobile} align={isMobile ? 'flex-start' : 'center'}>
        <Group noWrap={!isMobile} spacing={8}>
          {groupTitle && <H3Medium color="gray.7">{groupTitle}</H3Medium>}
          {groupTooltip && (
            <QuestionMarkTooltip withinPortal multiline width={200} label={groupTooltip} openDelay={100} />
          )}
        </Group>
        {infoBox}
      </Group>
      <Flex direction={isMobile ? 'column' : 'row'} gap={isMobile ? 12 : isTablet ? 16 : 32}>
        {!withoutLeftBorder && <Box w={isMobile ? '100%' : 8} h={isMobile ? 4 : 'auto'} bg="gray.0" />}
        <Stack
          w="100%"
          spacing={isTablet ? 12 : 24}
          sx={
            isMobile
              ? {
                  '& .mantine-SimpleGrid-root': {
                    gridTemplateColumns: 'minmax(0, 1fr) !important',
                  },
                  '& .mantine-InputWrapper-root': {
                    minWidth: 0,
                  },
                }
              : undefined
          }
        >
          {children}
        </Stack>
      </Flex>
    </Stack>
  );
}
